import { useMutation } from '@apollo/client';
import { Dispatch, FC, useCallback, useEffect, useRef } from 'react';
import {
  CREATE_MEDIA_METADATA,
  GENERATE_SIGNED_URL,
  GET_MEDIA_BY_FLOC,
} from '../../../graph-queries/tar/media';
import axios from 'axios';
import { Button, Progress } from '@bphxd/ds-core-react';
import classes from './MediaUpload.module.scss';
import { Img48, Info16, Reload24, Remove24, Upload32 } from '@bphxd/ds-core-react/lib/icons';
import { clsx } from 'clsx';
import {
  TMediaUploadAction,
  TMediaUploadState,
  useMediaUploadReducer,
} from './MediaUpload.reducer';
import { useDropzone } from 'react-dropzone';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';
import { useSelectedAsset } from '../../../lib/util';
import { MEDIA_ALLOWED_MIME_TYPES } from '../../../lib/constants/dbConstants';
import { MediaMetadataMimeEnum } from '../../../graph-queries/__generated__/graphql';

const MAX_FILE_SIZE_ALLOWED = 100_000_000;

export const MediaUploadRow: FC<{
  row: TMediaUploadState['fileRows'][number];
  dispatch: Dispatch<TMediaUploadAction>;
  onRetry: (file: File) => void;
}> = ({ row: { file, status, progress, error }, dispatch, onRetry }) => {
  return (
    <div className={clsx(classes.row, classes[status])}>
      <Img48 aria-label="Image preview" />

      <div className={classes.nameBlock}>
        <div className={classes.details}>
          <div className={classes.name}>{file.name}</div>

          <div className={clsx(classes.status, classes[status])}>
            {status === 'completed' && (
              <>
                <div className={classes.text}>{status}</div>
              </>
            )}
            {status === 'failed' && (
              <>
                <div className={classes.text} title={error?.message || status}>
                  {status}
                </div>
              </>
            )}
            {status === 'uploading' && (
              <>
                <div title={`${Math.round(progress * 100 * 100) / 100}%`} className={classes.text}>
                  {progress === 0
                    ? 'Waiting...'
                    : ` Uploaded ${Math.round(progress * 100 * 100) / 100 || 0}%`}
                </div>
              </>
            )}
          </div>
        </div>
        {status !== 'selected' && (
          <Progress
            rounded
            color={status === 'failed' ? 'light' : 'brand'}
            value={progress * 100}
          />
        )}
      </div>
      <div className={classes.rowActions}>
        {status === 'failed' && (
          <Reload24 className={classes.retry} title="Retry" onClick={() => onRetry(file)} />
        )}
        <Remove24
          aria-label="Cancel file"
          className={classes.remove}
          onClick={() => {
            dispatch({ type: 'cancel', file });
          }}
        />
      </div>
    </div>
  );
};

export const MediaUploadSelector: FC<{
  state: TMediaUploadState;
  dispatch: Dispatch<TMediaUploadAction>;
}> = ({ state, dispatch }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    dispatch({ type: 'init', fileList: acceptedFiles });
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  useEffect(() => {
    if (state.fileRows.length > 0 && inputRef.current) inputRef.current.value = '';
  }, [state.fileRows]);

  return (
    <>
      <div
        className={clsx({
          [classes.selector]: true,
          [classes.hide]: state.fileRows.length,
          [classes.drag]: isDragActive,
        })}
        {...getRootProps()}
      >
        <div className={classes.content}>
          <div className={classes.title}>
            <Upload32 aria-label="Upload" /> Drag and drop your files
          </div>
          <div className={classes.title}>or</div>
          <Button level="tertiary" size="xs">
            Browse
          </Button>
        </div>
        <div className={classes.notice}>
          <Info16 aria-label="Upload limit" /> Upload limit: 10 files per session Max file size: 100
          MB
        </div>
      </div>
      <input
        {...getInputProps()}
        data-testid="file-input"
        ref={inputRef}
        type="file"
        multiple
        onChange={(event) => {
          if (event.target.files) {
            dispatch({ type: 'init', fileList: event.target.files });
          }
        }}
      />
    </>
  );
};

const MediaUpload: FC<{ floc: string }> = ({ floc }) => {
  const [state, dispatch] = useMediaUploadReducer();
  const [generateUrl] = useMutation(GENERATE_SIGNED_URL);
  const asset = useSelectedAsset();
  const [saveMetaData] = useMutation(CREATE_MEDIA_METADATA, {
    refetchQueries: [GET_MEDIA_BY_FLOC],
  });
  const { sendSuccess, sendError } = useNoticeBar();

  const uploadFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE_ALLOWED) {
      const error = Error('File size exceeds 100MB');
      dispatch({ type: 'fail', file, error });
      return Promise.reject(error);
    }

    const mime = Object.entries(MEDIA_ALLOWED_MIME_TYPES).find(([key, val]) => {
      if (val === file.type) return key;
    })?.[0] as MediaMetadataMimeEnum;
    if (!mime) {
      const error = Error('Invalid file type');
      dispatch({ type: 'fail', file, error });
      return Promise.reject(error);
    }

    return generateUrl({ variables: { fileName: file.name, mime: file.type } })
      .then((res) => {
        if (!res.data) return Promise.reject('Upload URL not generated');

        const generated = res.data.generateSignedUrl;

        return axios
          .put(generated.url, file, {
            headers: { 'x-ms-blob-type': 'BlockBlob' },
            onUploadProgress: (progressEvent) => {
              dispatch({ type: 'progress', file, progress: progressEvent.progress || 0 });
            },
          })
          .then(() => generated);
      })
      .then((generated) => {
        return saveMetaData({
          variables: {
            input: {
              floc,
              fileName: file.name,
              mime,
              blobLocation: generated.blobLocation,
              assetId: asset?.assetId || -1,
            },
          },
        }).then((res) => {
          if (!res.data) return Promise.reject('Metadata not saved');

          return res.data.createMediaMetadata;
        });
      })
      .then(() => {
        dispatch({ type: 'complete', file });
      })
      .catch((error) => {
        console.error(error);
        dispatch({ type: 'fail', file, error });
        throw error;
      });
  };
  const retry = (file: File) => {
    dispatch({ type: 'start', file });
    uploadFile(file).catch(() => sendError('Upload failed. Remove invalid file to continue.'));
  };

  return (
    <div className={classes.root} title="Click or drag to upload">
      <MediaUploadSelector state={state} dispatch={dispatch} />
      <div
        className={clsx({
          [classes.uploader]: true,
          [classes.hide]: state.fileRows.length === 0,
        })}
      >
        <div className={classes.files}>
          {state.fileRows.map((row) => (
            <MediaUploadRow key={row.file.name} row={row} dispatch={dispatch} onRetry={retry} />
          ))}
        </div>
        <div className={classes.actions}>
          <Button
            size="sm"
            onClick={() =>
              Promise.all(state.fileRows.map((row) => uploadFile(row.file)))
                .then(() => {
                  sendSuccess('Your files have been uploaded successfully!');
                  dispatch({ type: 'reset' });
                })
                .catch(() => sendError('Upload failed. Remove invalid file to continue.'))
            }
          >
            Upload
          </Button>
          <Button
            size="sm"
            level="tertiary"
            onClick={() => {
              dispatch({ type: 'reset' });
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MediaUpload;
